<script lang="ts" setup>
import { computed, reactive, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import type { PropType } from 'vue'
import { useContainerTagsStore } from '~/stores/containerTags'
import { useContainerStore } from '~/stores/containers'

interface Operation {
  type: 'add' | 'remove'
  tag: string
  count: number
  total: number
}

const props = defineProps({
  containerNumbers: {
    type: Array as PropType<readonly string[]>,
    required: true,
  },
  modelValue: { type: Boolean, required: true },
})
const emit = defineEmits(['close', 'update:modelValue'])

const showAllTags = ref(false)
const saving = ref(false)

const tagStates = reactive(new Map<string, 'none' | 'partial' | 'full'>())
const originalTagMap = ref<Map<string, Set<string>>>(new Map())
const tagCounts = ref<Map<string, number>>(new Map())
const removeRequested = reactive(new Set<string>())

const MAX_DISPLAYED_TAGS = 6

const containerTagsStore = useContainerTagsStore()
const containerStore = useContainerStore()
const { tags: definedTags } = storeToRefs(containerTagsStore)

const containerObjects = computed(
  () =>
    props.containerNumbers
      .map((num) => containerStore.containerLookup.get(num))
      .filter((c) => !!c) as Array<{ number: string; tags: string[] }>
)

function resetState() {
  showAllTags.value = false
  removeRequested.clear()
  tagStates.clear()

  const map = new Map<string, Set<string>>()
  const counts = new Map<string, number>()
  const allTags = new Set<string>()

  containerObjects.value.forEach((c) => {
    const tagSet = new Set(c.tags)
    map.set(c.number, tagSet)
    c.tags.forEach((t) => {
      counts.set(t, (counts.get(t) || 0) + 1)
      allTags.add(t)
    })
  })

  originalTagMap.value = map
  tagCounts.value = counts

  const total = props.containerNumbers.length

  allTags.forEach((tag) => {
    const cnt = counts.get(tag) ?? 0
    if (cnt === total) {
      tagStates.set(tag, 'full')
    } else if (cnt > 0) {
      tagStates.set(tag, 'partial')
    }
  })
}

watch(
  () => props.modelValue,
  () => {
    resetState()
  }
)

watch(
  () => [...props.containerNumbers],
  () => {
    if (props.modelValue) {
      resetState()
    }
  }
)

const assignedTags = computed<string[]>(() =>
  Array.from(tagStates.entries())
    .filter(([, state]) => state !== 'none')
    .map(([tag]) => tag)
)

const tagOptionsWithCounts = computed(() =>
  definedTags.value.map((tag) => {
    const originalCount = tagCounts.value.get(tag) ?? 0
    const total = props.containerNumbers.length
    const state = tagStates.get(tag) ?? 'none'
    let label = tag

    if (state === 'full') {
      label = `${tag} (all)`
    } else if (state === 'partial') {
      label = `${tag} (${originalCount}/${total})`
    }

    const isSelected = state !== 'none'
    return { value: tag, label, tag, count: originalCount, total, isSelected }
  })
)

const availableTags = computed(() => definedTags.value)

const availableTagsSimplified = computed(() => {
  const sorted = availableTags.value
    .map((tag) => ({ tag, isSelected: assignedTags.value.includes(tag) }))
    .sort((a, b) =>
      a.isSelected === b.isSelected
        ? a.tag.localeCompare(b.tag)
        : a.isSelected
          ? -1
          : 1
    )

  if (showAllTags.value || sorted.length <= MAX_DISPLAYED_TAGS) return sorted

  const selected = sorted.filter((t) => t.isSelected)
  const extra = sorted
    .filter((t) => !t.isSelected)
    .slice(0, MAX_DISPLAYED_TAGS - selected.length)

  return [...selected, ...extra]
})

const operationPreview = computed<Operation[]>(() => {
  const ops: Operation[] = []
  if (!props.modelValue) return ops

  const total = props.containerNumbers.length

  const originallyOnMap = new Map<string, string[]>()
  originalTagMap.value.forEach((tagSet, containerNumber) => {
    tagSet.forEach((t) => {
      if (!originallyOnMap.has(t)) originallyOnMap.set(t, [])
      originallyOnMap.get(t)!.push(containerNumber)
    })
  })

  const allRelevant = new Set<string>([
    ...originallyOnMap.keys(),
    ...Array.from(tagStates.entries())
      .filter(([, s]) => s === 'full')
      .map(([t]) => t),
  ])

  allRelevant.forEach((tag) => {
    const originallyOn = originallyOnMap.get(tag) ?? []
    const state = tagStates.get(tag) ?? 'none'

    if (state === 'full') {
      const missing = props.containerNumbers.filter(
        (num) => !originallyOn.includes(num)
      )
      if (missing.length > 0) {
        ops.push({ type: 'add', tag, count: missing.length, total })
      }
    } else if (state === 'none') {
      if (originallyOn.length > 0) {
        ops.push({ type: 'remove', tag, count: originallyOn.length, total })
      }
    }
  })

  return ops
})

const hasChanges = computed(() => operationPreview.value.length > 0)

function onClickInputConfirm(tagName: string) {
  if (!tagName) return
  const state = tagStates.get(tagName) ?? 'none'

  if (state === 'partial') {
    tagStates.set(tagName, 'full')
  } else if (state === 'full') {
    tagStates.set(tagName, 'none')
  } else {
    tagStates.set(tagName, 'full')
  }
}

function onRemoveTag(tagName: string) {
  if (!tagName) return
  removeRequested.add(tagName)
  tagStates.set(tagName, 'none')
}

function handleTagSelectionChange(newValue: string[]) {
  const oldSet = new Set(assignedTags.value)
  const newSet = new Set(newValue)

  newSet.forEach((tag) => {
    if (!oldSet.has(tag)) {
      tagStates.set(tag, 'full')
    }
  })

  oldSet.forEach((tag) => {
    if (!newSet.has(tag)) {
      if (removeRequested.has(tag)) {
        removeRequested.delete(tag)
        return
      }
      const prevState = tagStates.get(tag) ?? 'none'
      if (prevState === 'partial') {
        tagStates.set(tag, 'full')
      } else if (prevState === 'full') {
        tagStates.set(tag, 'none')
      }
    }
  })
}

function close() {
  emit('close')
  emit('update:modelValue', false)
}

function save() {
  saving.value = true

  const containerTagMap = new Map<string, Set<string>>()
  containerObjects.value.forEach((c) =>
    containerTagMap.set(c.number, new Set(c.tags))
  )

  const tagAdditionMap = new Map<string, string[]>()
  const tagRemovalMap = new Map<string, string[]>()

  const originallyOnMap = new Map<string, string[]>()
  originalTagMap.value.forEach((tagSet, containerNumber) => {
    tagSet.forEach((t) => {
      if (!originallyOnMap.has(t)) originallyOnMap.set(t, [])
      originallyOnMap.get(t)!.push(containerNumber)
    })
  })

  const allRelevant = new Set<string>([
    ...originallyOnMap.keys(),
    ...Array.from(tagStates.entries())
      .filter(([, s]) => s === 'full')
      .map(([t]) => t),
  ])

  allRelevant.forEach((tag) => {
    const originallyOn = originallyOnMap.get(tag) ?? []
    const state = tagStates.get(tag) ?? 'none'

    if (state === 'full') {
      const missing = props.containerNumbers.filter(
        (num) => !originallyOn.includes(num)
      )
      if (missing.length > 0) {
        tagAdditionMap.set(tag, missing)
      }
    } else if (state === 'none') {
      if (originallyOn.length > 0) {
        tagRemovalMap.set(tag, originallyOn)
      }
    }
  })

  tagAdditionMap.forEach((containers, tag) => {
    containerTagsStore.tagContainers(containers, [tag])
    if (!containerTagsStore.tags.includes(tag)) {
      containerTagsStore.tags.push(tag)
    }
  })

  tagRemovalMap.forEach((containers, tag) => {
    containerTagsStore.untagContainers(containers, [tag])
  })

  saving.value = false
  close()
}
</script>

<template>
  <el-dialog
    :model-value="props.modelValue"
    :title="`Edit Tags for ${props.containerNumbers.length} container${props.containerNumbers.length > 1 ? 's' : ''}`"
    width="40%"
    :append-to-body="true"
    @update:model-value="emit('update:modelValue')"
    @close="emit('close')"
  >
    <div class="space-y-4">
      <el-select
        :model-value="assignedTags"
        multiple
        placeholder="Select tags to apply to containers"
        allow-create
        filterable
        size="large"
        class="w-full"
        @remove-tag="onRemoveTag"
        @update:model-value="handleTagSelectionChange"
      >
        <el-option
          v-for="option in tagOptionsWithCounts"
          :key="option.tag"
          :value="option.value"
          :label="option.label"
        >
          {{ option.label }}
        </el-option>
      </el-select>

      <div v-if="availableTagsSimplified.length">
        <h3 class="text-sm font-medium mb-2">Available tags:</h3>
        <div class="flex flex-wrap gap-2">
          <el-tag
            v-for="item in availableTagsSimplified"
            :key="item.tag"
            class="cursor-pointer"
            @click="onClickInputConfirm(item.tag)"
          >
            {{ item.tag }}
          </el-tag>

          <el-tag
            v-if="availableTags.length > MAX_DISPLAYED_TAGS"
            class="cursor-pointer"
            type="info"
            plain
            @click="showAllTags = !showAllTags"
          >
            {{
              showAllTags
                ? 'Show less'
                : `+${availableTags.length - MAX_DISPLAYED_TAGS}`
            }}
          </el-tag>
        </div>
      </div>

      <div v-if="operationPreview.length" class="bg-gray-50 p-3 rounded">
        <h4 class="text-sm font-medium mb-2">Changes to be made:</h4>
        <ul class="text-sm space-y-1">
          <li
            v-for="op in operationPreview"
            :key="`${op.type}-${op.tag}`"
            class="flex items-center"
          >
            <i-mdi:plus-circle
              v-if="op.type === 'add'"
              class="text-green-600 mr-2"
            />
            <i-mdi:minus-circle
              v-if="op.type === 'remove'"
              class="text-red-600 mr-2"
            />
            <span>
              <strong>{{ op.tag }}</strong>
              {{
                op.type === 'add' ? 'will be added to' : 'will be removed from'
              }}
              {{ op.count }} container{{ op.count > 1 ? 's' : '' }}
            </span>
          </li>
        </ul>
      </div>
    </div>

    <template #footer>
      <div class="flex">
        <el-button class="flex-1" @click="close">Cancel</el-button>
        <el-button
          class="flex-1"
          type="primary"
          :loading="saving"
          :disabled="!hasChanges"
          @click="save"
        >
          Save
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
